/**
 * Preview Panel Stats - displays item statistics and metadata
 */

import { Skeleton } from '@loader/ui';
import type { WorkspaceItemType, TableStatus, LinkedTableStatus } from '@loader/shared-types';

import { StatusSelector } from './shared';

interface PreviewPanelStatsProps {
  type: WorkspaceItemType;
  columnCount?: number;
  versionCount?: number;
  sourceTablesCount?: number;
  joinsCount?: number;
  filtersCount?: number;
  calculatedColumnsCount?: number;
  status?: TableStatus | LinkedTableStatus;
  onStatusChange?: (status: string) => void;
  isUpdatingStatus?: boolean;
  isLoading?: boolean;
  /** Whether user can edit - viewers see read-only status */
  canEdit?: boolean;
}

export function PreviewPanelStats({
  type,
  columnCount,
  versionCount,
  sourceTablesCount,
  joinsCount,
  filtersCount,
  calculatedColumnsCount,
  status,
  onStatusChange,
  isUpdatingStatus,
  isLoading,
  canEdit = true,
}: PreviewPanelStatsProps) {
  if (isLoading) {
    return (
      <div className="px-4 py-3 space-y-2">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-4 w-32" />
      </div>
    );
  }

  return (
    <div className="px-4 py-3 text-sm">
      {type === 'table' && (
        <div className="text-muted-foreground flex items-center justify-between">
          {columnCount !== undefined && <span>{columnCount} columns</span>}
          {versionCount !== undefined && versionCount > 0 && <span>Version {versionCount}</span>}
          {status && (
            <StatusSelector
              status={status}
              onStatusChange={onStatusChange}
              isPending={isUpdatingStatus}
              canEdit={canEdit}
            />
          )}
        </div>
      )}

      {type === 'linked_table' && (
        <div className="text-muted-foreground flex items-center justify-between">
          {sourceTablesCount !== undefined && (
            <span>{sourceTablesCount} table{sourceTablesCount !== 1 ? 's' : ''}</span>
          )}
          {joinsCount !== undefined && joinsCount > 0 && (
            <span>{joinsCount} join{joinsCount !== 1 ? 's' : ''}</span>
          )}
          {filtersCount !== undefined && filtersCount > 0 && (
            <span>{filtersCount} filter{filtersCount !== 1 ? 's' : ''}</span>
          )}
          {calculatedColumnsCount !== undefined && calculatedColumnsCount > 0 && (
            <span>{calculatedColumnsCount} calc</span>
          )}
          {status && (
            <StatusSelector
              status={status}
              onStatusChange={onStatusChange}
              isPending={isUpdatingStatus}
              canEdit={canEdit}
            />
          )}
        </div>
      )}
    </div>
  );
}
